'use client'

import { useState, useMemo } from 'react'
import { MagnifyingGlassIcon, XIcon, FunnelIcon } from '@phosphor-icons/react'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover'
import { cn } from '@/lib/tailwind'
import type { Entry } from '@/document'
import { useLibraryData } from './select-library'
import { FilterDropdown } from './FilterDropdown'
import { BooksGallery } from './LibraryGalleries'
import {
  filterBooks,
  searchBooks,
  getBookStatusOptions,
  type BookFilterState,
} from './library-utils'

function BooksSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="bg-muted h-7 w-24 animate-pulse rounded" />
          <div className="bg-muted mt-1.5 h-4 w-16 animate-pulse rounded" />
        </div>
        <div className="bg-muted h-11 w-full animate-pulse rounded-lg sm:h-8 sm:w-40" />
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="bg-muted aspect-[2/3] animate-pulse rounded-lg" />
            <div className="bg-muted h-4 w-3/4 animate-pulse rounded" />
            <div className="bg-muted h-3 w-1/2 animate-pulse rounded" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function BooksView() {
  const { data, isLoading } = useLibraryData()

  if (isLoading || !data) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <BooksSkeleton />
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      <BooksPageContent books={data.books} />
    </div>
  )
}

function BooksPageContent({ books }: { books: Entry[] }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<BookFilterState>({ status: [] })

  const statusOptions = useMemo(() => getBookStatusOptions(books), [books])

  const searched = useMemo(
    () => searchBooks(books, searchQuery),
    [books, searchQuery],
  )
  const filtered = useMemo(
    () => filterBooks(searched, filters),
    [searched, filters],
  )

  const hasActiveFilters = filters.status.length > 0
  const displayCount = filtered.length
  const isSearching = searchQuery.trim().length > 0

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex items-center justify-between sm:block">
          <div>
            <h1 className="text-xl font-semibold tracking-tight sm:text-xl">
              Books
            </h1>
            <p className="text-muted-foreground mt-0.5 text-sm sm:text-xs">
              {isSearching || hasActiveFilters
                ? `${displayCount} of ${books.length}`
                : `${books.length} items`}
            </p>
          </div>
          {/* Mobile filter toggle */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  'flex h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-colors sm:hidden',
                  hasActiveFilters
                    ? 'bg-foreground/10 text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
                )}
              >
                <FunnelIcon className="h-4 w-4" />
                {hasActiveFilters && (
                  <span className="bg-foreground text-background flex h-4 w-4 items-center justify-center rounded-full text-[10px]">
                    {filters.status.length}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
              <MobileBookFilters
                filters={filters}
                setFilters={setFilters}
                statusOptions={statusOptions}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-auto">
          <MagnifyingGlassIcon
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 sm:left-2.5 sm:h-3.5 sm:w-3.5"
            aria-hidden="true"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search books..."
            className="placeholder:text-muted-foreground border-input bg-background focus:ring-ring h-11 w-full rounded-lg border py-1 pr-8 pl-10 text-base transition-colors outline-none focus:ring-2 sm:h-8 sm:w-40 sm:rounded-md sm:pr-7 sm:pl-8 sm:text-xs"
            aria-label="Search books"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2.5 -translate-y-1/2"
              aria-label="Clear search"
            >
              <XIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Desktop filters */}
      <div className="hidden items-center gap-1 border-b pb-3 sm:flex sm:pb-2">
        <FunnelIcon className="text-muted-foreground mr-1 h-3.5 w-3.5" />
        <FilterDropdown
          label="Status"
          options={statusOptions}
          selected={filters.status}
          onChange={(status) => setFilters({ status })}
        />
        {hasActiveFilters && (
          <button
            onClick={() => setFilters({ status: [] })}
            className="text-muted-foreground hover:text-foreground ml-1 flex h-6 items-center gap-1 rounded px-1.5 text-xs transition-colors"
          >
            <XIcon className="h-3 w-3" />
            Clear
          </button>
        )}
      </div>

      <BooksGallery books={filtered} />
    </div>
  )
}

function MobileBookFilters({
  filters,
  setFilters,
  statusOptions,
}: {
  filters: BookFilterState
  setFilters: (f: BookFilterState) => void
  statusOptions: { value: string; label: string }[]
}) {
  const toggleFilter = (current: string[], value: string) => {
    return current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]
  }

  return (
    <div className="space-y-3">
      {statusOptions.length > 0 && (
        <div>
          <p className="text-muted-foreground mb-1.5 text-[10px] font-medium tracking-wide uppercase">
            Status
          </p>
          <div className="flex flex-wrap gap-1">
            {statusOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() =>
                  setFilters({
                    status: toggleFilter(filters.status, opt.value),
                  })
                }
                className={cn(
                  'rounded-md px-2 py-1 text-xs transition-colors',
                  filters.status.includes(opt.value)
                    ? 'bg-foreground/10 text-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent/50',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
